import { useState } from "react";
import { MoreHorizontal, EyeOff, Eye, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface PromptActionsMenuProps {
  submissionId: string;
  promptUserId: string;
  isPublic: boolean;
  onHide?: () => void;
  onDelete?: () => void;
}

export function PromptActionsMenu({
  submissionId,
  promptUserId,
  isPublic,
  onHide,
  onDelete,
}: PromptActionsMenuProps) {
  const { user, membership } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  // Permission check: only creator or admin/client_admin
  const isOwner = user?.id === promptUserId;
  const isAdmin =
    membership?.role === "admin" || membership?.role === "client_admin";

  if (!isOwner && !isAdmin) return null;

  const handleToggleVisibility = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    const { error } = await supabase
      .from("fluency_submissions")
      .update({ is_public: !isPublic })
      .eq("id", submissionId);

    setLoading(false);
    if (error) {
      console.error("Failed to update visibility:", error);
      toast.error("Failed to update prompt visibility");
      return;
    }
    toast.success(isPublic ? "Prompt hidden from feed" : "Prompt visible in feed");
    onHide?.();
  };

  const handleDelete = async () => {
    setLoading(true);
    const { error } = await supabase
      .from("fluency_submissions")
      .update({ is_deleted: true })
      .eq("id", submissionId);

    setLoading(false);
    setShowDeleteDialog(false);
    if (error) {
      console.error("Failed to delete prompt:", error);
      toast.error("Failed to delete prompt");
      return;
    }
    toast.success("Prompt deleted");
    onDelete?.();
  };

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <DropdownMenu>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => e.stopPropagation()}
                  disabled={loading}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Prompt actions</p>
            </TooltipContent>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={handleToggleVisibility} disabled={loading}>
                {isPublic ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide from Feed
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show in Feed
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteDialog(true);
                }}
                disabled={loading}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Prompt
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this prompt?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the prompt from the feed and your library. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
